import dayjs, { Dayjs } from "dayjs";
import { useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import { FiCheck } from "react-icons/fi";
import Bootstrap5Row from "src/components/shared/elements/Bootstrap5Row";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import { BROWSER_TIME_ZONE, DATE_TIME_FORMATS } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { daysjsFromUnix, handleError, isEmptyObject, notify } from "src/utils";

const CAL_TIME_CONTAINER_CLASS = "cal-times-item-container";

interface IDisplayTimeBlock {
    time: string;
    startUnix: number;
    endUnix: number;
    practitionerId?: number;
    selected: boolean;
    schedulerConfigId?: number;
}

interface ITimeBlockHolder {
    am: IDisplayTimeBlock[];
    pm: IDisplayTimeBlock[];
}

const PatientSchedulingTimeBlocks: React.FC = () => {
    const { selfScheduleSettings, selfSchedule, setSelfSchedule, availability } =
        usePatientSelfSchedulingContext();

    const selectedDateFormatted = useMemo(() => {
        if (selfSchedule?.date)
            return dayjs(selfSchedule.date).format(DATE_TIME_FORMATS.Appointment.DateFormat2NoYear);
        return "";
    }, [selfSchedule?.date]);

    const setSelectedTime = useCallback(
        (
            startUnix: number,
            _endUnix: number,
            practitionerId?: number,
            schedulerConfigId?: number,
        ) => {
            // TODO: SCHEDULER_2 practitioner is gonna be selected at this point?
            if (!selfSchedule) return;
            if (!selfScheduleSettings && !selfSchedule?.practitionerId && !practitionerId) return;
            // availability.schedule
            setSelfSchedule({
                ...selfSchedule,
                time: startUnix,
                practitionerId: practitionerId || selfSchedule.practitionerId,
                ...(schedulerConfigId ? { schedulerConfigId } : {}),
            });
        },
        [selfSchedule, setSelfSchedule, selfScheduleSettings],
    );

    const render = useCallback(
        (timeSlot: IDisplayTimeBlock) => {
            const isSelected = selfSchedule?.time === timeSlot.startUnix;

            const klass = timeSlot.selected
                ? `${CAL_TIME_CONTAINER_CLASS} text-primary text-center mx-auto selected`
                : `${CAL_TIME_CONTAINER_CLASS} text-primary text-center mx-auto`;

            const check = timeSlot.selected ? (
                <FiCheck
                    style={{ padding: 1 }}
                    className="bg-primary text-white rounded-circle m-1"
                />
            ) : (
                <FiCheck style={{ padding: 1 }} className="invisible rounded-circle m-1" />
            );

            return (
                <div key={timeSlot.startUnix} className="col-4">
                    <Button
                        className={klass}
                        onClick={() => {
                            setSelectedTime(
                                timeSlot.startUnix,
                                timeSlot.endUnix,
                                timeSlot.practitionerId,
                                timeSlot.schedulerConfigId,
                            );
                        }}
                    >
                        <Bootstrap5Row className="time align-items-center justify-content-center">
                            {isSelected ? check : null}
                            {timeSlot.time}
                        </Bootstrap5Row>
                    </Button>
                </div>
            );
        },
        [selfSchedule, setSelectedTime],
    );

    const toTimeString = (time: Dayjs) => {
        return time.format(DATE_TIME_FORMATS.Scheduler.BlockTime12HFormatCondensed);
    };

    const blocks = useMemo(() => {
        const am_pm = {
            am: [] as IDisplayTimeBlock[],
            pm: [] as IDisplayTimeBlock[],
        } as ITimeBlockHolder;

        if (!selfSchedule?.date || !availability) return am_pm;

        const dateKey = dayjs(selfSchedule.date).format(
            DATE_TIME_FORMATS.Scheduler.ScheduleDateFormat,
        );
        const daySlots = availability.schedule.slotsByDate?.[dateKey] || [];
        const nowUnix = dayjs().unix();
        // filter out any slots that are in the past, or too close to the current time based on self scheduling settings
        let filteredSlots = selfSchedule.practitionerId
            ? daySlots.filter((slot) => {
                  return (
                      slot.startUnix > nowUnix &&
                      (slot.practitionerId === selfSchedule.practitionerId ||
                          (selfSchedule.practitionerId !== null &&
                              slot.practitionerToConfig?.[selfSchedule.practitionerId]))
                  );
              })
            : daySlots;

        if (
            selfScheduleSettings?.afterwardBookingLimit ||
            selfScheduleSettings?.forwardBookingLimit
        ) {
            filteredSlots = filteredSlots.filter((slot) => {
                let included = true;
                if (selfScheduleSettings?.afterwardBookingLimit) {
                    const limit = dayjs().add(
                        selfScheduleSettings.afterwardBookingLimit,
                        selfScheduleSettings.afterwardBookingLimitUnit,
                    );
                    if (limit.isBefore(dayjs(slot.endUnix))) {
                        included = false;
                    }
                }
                if (selfScheduleSettings?.forwardBookingLimit) {
                    const limit = dayjs().add(
                        selfScheduleSettings.forwardBookingLimit,
                        selfScheduleSettings.forwardBookingLimitUnit,
                    );
                    if (limit.isAfter(dayjs(slot.startUnix))) {
                        included = false;
                    }
                }
                return included;
            });
        }

        filteredSlots.forEach((slot) => {
            const startTime = daysjsFromUnix(slot.startUnix, BROWSER_TIME_ZONE);
            let schedulerConfigId = undefined;
            if (slot.practitionerToConfig) {
                if (!selfSchedule.practitionerId) {
                    handleError(
                        new Error(
                            "selfSchedule.practitionerId has not been set in selfSchedule context which is a INVALID STATE, selfSchedule.practitionerId must be selected to retrieve the slot from slot.practitionerToConfig",
                        ),
                    );
                    notify({
                        title: "Error",
                        message: "An error occurred, please refresh the page",
                        level: "error",
                    });
                    return;
                }
                schedulerConfigId = slot.practitionerToConfig[selfSchedule.practitionerId];
            } else if (slot.schedulerConfigId) {
                schedulerConfigId = slot.schedulerConfigId;
            }
            // depends if practitioner is selectedable
            const displayBlock: IDisplayTimeBlock = {
                startUnix: slot.startUnix,
                endUnix: slot.endUnix,
                time: toTimeString(startTime),
                practitionerId: slot.practitionerId,
                selected: selfSchedule.time === slot.startUnix,
                schedulerConfigId,
            };

            if (startTime.hour() < 12) {
                am_pm.am.push(displayBlock);
            } else {
                am_pm.pm.push(displayBlock);
            }
        });
        return am_pm;
    }, [availability, selfSchedule, selfScheduleSettings]);

    const renderPM = useCallback(() => {
        return blocks.pm.map((block: IDisplayTimeBlock) => render(block));
    }, [blocks.pm, render]);

    const renderAM = useCallback(() => {
        return blocks.am.map((block: IDisplayTimeBlock) => render(block));
    }, [blocks.am, render]);

    if (!availability) {
        return <FullScreenLoadingSpinner message={"Loading Available Time Slots..."} />;
    }

    if (!selfSchedule) return null;

    return (
        <div className={"cal-times-container mt-4"}>
            {!isEmptyObject(blocks.am) && (
                <>
                    <div className="row cal-times ">
                        <div className="col body-large bold text-left">
                            <p className="pl-3">AM</p>
                        </div>
                        <div className="col body-small gray2 text-right">
                            <div className="pt-1">{selectedDateFormatted}</div>
                        </div>
                    </div>
                    <div className={"cal-times row"}>{renderAM()}</div>
                </>
            )}
            {!isEmptyObject(blocks.pm) && (
                <>
                    <div className="row cal-times mt-4">
                        <div className="col body-large bold text-left">
                            <p className="pl-3">PM</p>
                        </div>
                        <div className="col body-small text-right gray2">
                            <div className="pt-1">{selectedDateFormatted}</div>
                        </div>
                    </div>
                    <div className={"cal-times row"}>
                        <>{renderPM()}</>
                    </div>
                </>
            )}
        </div>
    );
};

export default PatientSchedulingTimeBlocks;
